import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  createContext,
  useContext,
} from 'react';
import { useNavigate } from "react-router-dom";
// dxSwitch editor registration is handled centrally in src/devextreme-editors.js
import Form, {
  SimpleItem,
  Label,
  PatternRule,
  RequiredRule,
  GroupItem,
  EmptyItem,
  ColCountByScreen,
  Item,
} from 'devextreme-react/form';
import DxForm from "devextreme/ui/form";
import { LoadPanel } from 'devextreme-react/load-panel';
import { confirm } from 'devextreme/ui/dialog';
import { AddressAutoComplete, Badge, CardContainer, EmploymentStatuses } from '../../components';
import { useMemberApiService } from '../../services';
import {
  showError,
  showSuccess,
  updateStateAddressFields,
} from '../../helpers/ui';
import { useScreenSize } from '../../utils/media-query';
import { appConst } from '../../app-const';
import { useGlobalState } from '../../contexts/global-state';
import { usePageTitle } from '../../contexts/page-title';
import { useEditProfile } from '../../contexts/edit-profile';

import './edit-profile.scss';

// The Mailing Address caption shows a Verified/Unverified badge. The caption
// render function is fixed (defined here, not inside the component) and the
// badge reads the state from context, so a change of state only re-renders the
// badge: the Form's item options never change, so it doesn't rebuild the group
// (which would re-mount the address box mid-typing).
const AddressVerifiedContext = createContext(false);

function MailingAddressCaption({ caption }) {
  const verified = useContext(AddressVerifiedContext);
  return (
    <span className="caption-with-badge">
      {caption}
      {verified
        ? <Badge text="Verified" color="green" />
        : <Badge text="Unverified" color="red" />}
    </span>
  );
}

const renderMailingAddressCaption = (data) => <MailingAddressCaption caption={data.caption} />;

export default function ({ memberId }) {
  const {
    getByIdAsync,
    updateByIdAsync
  } = useMemberApiService();
  const [member, setMember] = useState();
  const [fetchingData, setFetchingData] = useState(true);
  const [savingData, setSavingData] = useState(false);
  const [city, setCity] = useState();
  const [province, setProvince] = useState();
  const { isLarge, isExtraLarge, isSmall, isXSmall } = useScreenSize();
  const { lookupData } = useGlobalState();
  const navigate = useNavigate();
  const { setTitle } = usePageTitle();
  const { setEditProfileInfo } = useEditProfile();

  // "Edited" = the member differs from what was loaded. The header shows
  // Confirm Up-to-Date while unedited and Save/Undo once anything changes.
  const [isDirty, setIsDirty] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const loadedSnapshotRef = useRef(null);
  // Typing in the address box changes no form data until a suggestion is picked.
  const addressEditedRef = useRef(false);

  const checkDirty = useCallback((current) => {
    setIsDirty(addressEditedRef.current
      || (!!current && JSON.stringify(current) !== loadedSnapshotRef.current));
  }, []);

  const salutationEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.salutations,
  };

  const genderEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.genders
  };

  const pronounEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.pronouns,
  };

  const provinceEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.provinces,
    onValueChanged: (e) => {
      setProvince(e.component.option("text"));
    }
  }

  const phoneNumberTypeEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.phoneNumberTypes,
    grouped: false,
  };

  const emailAddressTypeEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    items: lookupData.emailAddressTypes,
    grouped: false,
  };

  const designationEditorOptions = {
    ...appConst.defaultSelectBoxOptions,
    grouped: true,
    items: lookupData.designations,
  };

  useLayoutEffect(() => setTitle("Edit Profile"), [setTitle]);

  const handleFetchMember = useCallback(async () => {
    setFetchingData(true);
    const { data } = await getByIdAsync(memberId);
    if (data) {
      // Snapshot before setMember, so the [member] effect sees it as unedited
      // (this is also how Undo returns the header to Confirm Up-to-Date).
      loadedSnapshotRef.current = JSON.stringify(data);
      addressEditedRef.current = false;
      setAddressVerified(!!data.addressVerified);
      setMember(data);
      setFetchingData(false);
    }
  }, [getByIdAsync, memberId]);

  const undo = async () => {
    const result = confirm("<i>Are you sure to undo all the changes?</i>", "Confirm changes");
    result.then(async (dialogResult) => {
      if (!dialogResult) {
      } else {
        await handleFetchMember();
      }
    });
  }

  useEffect(() => {
    (async () => {
      try {
        if (memberId) {
          await handleFetchMember();
          return;
        }
        throw new Error("Your member profile could not be loaded.");
      }
      catch (error) {
        showError(error.message);
        setFetchingData(false);
        navigate(-1);
      }
    })();
  }, [getByIdAsync, memberId, navigate, handleFetchMember]);

  // Adds/removes (emails, phones, work locations, designations) replace the
  // member object; in-place edits call checkDirty directly.
  useEffect(() => {
    if (member) checkDirty(member);
  }, [member, checkDirty]);

  useEffect(() => {
    setEditProfileInfo(prev => (prev.isDirty === isDirty ? prev : { ...prev, isDirty }));
  }, [isDirty, setEditProfileInfo]);

  async function saveProfile() {
    let form = getFormInstance("memberForm");
    let res = form.validate();
    let isValid = res.isValid;

    if (!isValid) {
      res.brokenRules[0].validator.focus();
      return false;
    }

    const addressNameAndVerfication = addressAutoCompleteRef.current.getAddress();

    if (!addressNameAndVerfication.addressVerified) {
      showError("Your address has not been verified yet. Please, try to re-enter your address and select a verified address from the list.");
      return;
    }

    const result = confirm("Are you sure you have reviewed ALL of your information?", "Confirm changes");
    result.then(async (dialogResult) => {
      if (!dialogResult) {
      } else {
        try {
          setSavingData(true);
          let data = {
            firstName: member.firstName,
            lastName: member.lastName,
            preferredFirstName: member.preferredFirstName,
            previousLastName: member.previousLastName,
            address1: member.address1,
            address2: member.address2,
            city: member.city,
            provinceId: member.provinceId,
            postalCode: member.postalCode,
            pronounsId: member.pronounsId,
            salutationId: member.salutationId,
            emailAddresses: member.emailAddresses,
            phoneNumbers: member.phoneNumbers,
            birthDate: member.birthDate,
            certificateNumber: member.certificateNumber,
            startDate: member.startDate,
            genderId: member.genderId,
            addressVerified: addressNameAndVerfication.addressVerified,
            schools: member.schools.concat(member.previousSchools),
            designations: member.designations,
            employmentStatusId: member.employmentStatusId,
            changedEmploymentStatusEffectiveDate: member.changedEmploymentStatusEffectiveDate,
          }
          const response = await updateByIdAsync(member.id, data);
          if (response.data) {
            showSuccess('Your profile has been updated.')
            navigate(-1);
          } else {
            showError('An error occurred while saving your profile.');
          }
        } catch (error) {
          setFetchingData(false);
          showError(error.message);
        } finally {
          setSavingData(false);
        }
      }
    });
  }

  function setPreferredEmailOrPhone(index, property) {
    if (!member[property].length) return;
    // Only one item can be preferred. When the user turns a toggle on, clear the
    // others by updating just those editors through the form instance — do NOT
    // replace formData (setMember), which repaints the whole Form and makes the
    // ScrollView jump to the top.
    if (!member[property][index].preferred) return;
    const form = getFormInstance('memberForm');
    member[property].forEach((item, i) => {
      if (i !== index && item.preferred) {
        item.preferred = false;
        // e.event is undefined for this programmatic change, so the switch's
        // onValueChanged guard prevents recursion back into this function.
        form?.updateData(`${property}[${i}].preferred`, false);
      }
    });
  }

  function getFormInstance(id) {
    let formEl = document.getElementById(id);
    return DxForm.getInstance(formEl);
  }

  function updateAddressFields(addressObj) {
    const { provinces } = lookupData;
    // Replacing formData repaints the Form and the ScrollView jumps, so restore it.
    setMemberAndKeepScrollPosition(updateStateAddressFields(addressObj, member, provinces));
  }

  const addressAutoCompleteRef = useRef();

  function setMemberAndKeepScrollPosition(member) {
    // Target the page's ScrollView, not the side-nav's scrollable (which comes first in the DOM).
    const getContainer = () => document.querySelector('.layout-scroll .dx-scrollable-container');
    const scrollTop = getContainer()?.scrollTop ?? 0;
    setMember(member);
    // The Form repaints after React commits, possibly over several frames, so keep
    // restoring the position until it has settled.
    const start = performance.now();
    const restore = () => {
      const container = getContainer();
      if (container && container.scrollTop !== scrollTop) container.scrollTop = scrollTop;
      if (performance.now() - start < 400) requestAnimationFrame(restore);
    };
    requestAnimationFrame(restore);
  }

  function updatePrimaryToFalse(index, endDate, selectedSAToBePrimary) {
    //This function is used to end a school association and
    //it is used to to set a new main location if the parameter selectedSAToBePrimary
    //is passed.
    let temp = { ...member };
    const schools = member.schools;
    const previousSchools = member.previousSchools;

    if (selectedSAToBePrimary!==null) {
      if (typeof temp.schools[selectedSAToBePrimary] !== 'undefined') {
        schools[selectedSAToBePrimary].primary = true;
      }
    }

    let el;
    if (index > -1) {
      el = schools[index];
      el.primary = false;
      el.endDate = endDate;
      previousSchools.push(el);
      schools.splice(index, 1);
      temp.previousSchools = previousSchools;
    }

    temp.schools = schools;
    setMemberAndKeepScrollPosition(temp);
  }

  function updatePrinaryToTrue(index) {
    //this function is used to set a main location
    let temp = { ...member };

    if (index > -1) {
      temp.schools[index].primary = true;
      temp.schools.forEach((school, i) => {
        if (i !== index) {
          school.primary = false;
        }
      });
      setMemberAndKeepScrollPosition(temp);
    }
  }

  function updateSADivision(index, divisionId) {
    if (index > -1) {
      member.schools[index].divisionId = divisionId;
      checkDirty(member);
    }
  }

  function updateSAStartDate(index, startDate) {
    if (index > -1) {
      member.schools[index].startDate = startDate;
      checkDirty(member);
    }
  }

  function addNewSA(sa) {
    //this function is used to add a new school association and
    //to end a location if the object has the two properties saIndexToEnd and selectedEndDate
    const b = Object.hasOwn(sa, 'saIndexToEnd') && Object.hasOwn(sa, 'selectedEndDate');
    if (b) {
      updatePrimaryToFalse(sa.saIndexToEnd, sa.selectedEndDate)
    }
    let temp = { ...member };
    if (sa.primary) {
      temp.schools.forEach((school, i) => {
        school.primary = false;
      });
    }
    const newSa = {
      schoolId: sa.schoolId,
      startDate: sa.startDate,
      divisionId: sa.divisionId,
      endDate:null,
      primary: sa.primary,
      school: { id: sa.schoolId, name: sa.schoolName }
    }
    temp.schools.push(newSa);
    setMemberAndKeepScrollPosition(temp);
  }

  // The header gets these once (below), so route through refs that are
  // refreshed every render; otherwise it would call a stale saveProfile/undo
  // holding an old `member`.
  const saveProfileRef = useRef(saveProfile);
  saveProfileRef.current = saveProfile;
  const undoRef = useRef(undo);
  undoRef.current = undo;

  // Memoized so re-renders that don't change what the form shows (the edited
  // flag, the address-verified badge, context updates) don't hand DevExtreme's
  // Form new item options: it would rebuild every editor, losing focus and any
  // typing not yet in formData (e.g. the address box). Rebuilt when the member
  // object is replaced (load, undo, add/remove), or lookups/layout change.
  const memberForm = useMemo(() => (
      <Form
        id='memberForm'
        formData={member}
        showValidationSummary={true}
        height="100%"
        className={(isSmall || isXSmall) ? "member-form-small" : "member-form"}
        onFieldDataChanged={() => checkDirty(member)}
        onOptionChanged={(e) => {
          if (member) {
            if (!e.component.isNotFirstLoad) {
              e.component.isNotFirstLoad = true;
              setEditProfileInfo({
                recordReviewedByMember: member.recordReviewedByMember,
                form: e.component,
                // The review bar's Confirm Up-to-Date and Save both call this.
                save: () => saveProfileRef.current(),
                undo: () => undoRef.current(),
                isDirty: false,
              })
            }
          }
        }}
      >
        <GroupItem cssClass="personal_information">
          <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
          <Item colSpan={2}>
            <h1>Personal Information</h1>
          </Item>
          <SimpleItem dataField='firstName' editorType='dxTextBox' editorOptions={{ placeholder:'(e.g. William James)'}}>
            <Label text='Legal First Name' />
            <RequiredRule message='First Name is required.' />
          </SimpleItem>
          <SimpleItem dataField='lastName' editorType='dxTextBox'>
            <Label text='Last Name' />
            <RequiredRule message='Last Name is required.' />
          </SimpleItem>
          <SimpleItem dataField='preferredFirstName' editorType='dxTextBox' editorOptions={{ placeholder:'(e.g.Bill)' }}>
            <Label text='Preferred First Name' />
          </SimpleItem>
          <SimpleItem dataField='previousLastName' editorType='dxTextBox'>
            <Label text='Previous Last Name' />
          </SimpleItem>
          <SimpleItem
            dataField='salutationId'
            editorType='dxSelectBox'
            editorOptions={salutationEditorOptions}
          >
            <Label text='Salutation' />
          </SimpleItem>
          <SimpleItem
            dataField='genderId'
            editorType='dxSelectBox'
            editorOptions={genderEditorOptions}
            visible={false}
          >
            <Label text='Gender' />
          </SimpleItem>
          <SimpleItem
            dataField='pronounsId'
            editorType='dxSelectBox'
            editorOptions={pronounEditorOptions}
            visible={true}
          >
            <Label text='Pronouns' />
          </SimpleItem>
          <SimpleItem
            dataField='birthDate'
            editorType='dxDateBox'
            editorOptions={appConst.defaultSelectDateBoxOptions}
          >
            <Label text='Date of Birth (YYYY-MM-DD)' />
          </SimpleItem>
          <SimpleItem dataField="certificateNumber" editorType="dxTextBox">
            <Label text='Teaching Certificate Number' />
          </SimpleItem>
          <Item>
            <Label text='Employment Status' />
            <EmploymentStatuses
              defaultValue={member && member.employmentStatusId}
              setEmploymentValues={(v, d) => {
                member.employmentStatusId = v;
                member.changedEmploymentStatusEffectiveDate = d;
                checkDirty(member);
              }} />
          </Item>
          <EmptyItem />
          <EmptyItem />
        </GroupItem>
        <GroupItem cssClass="contact_information">
          <Item>
            <h1>Contact Information</h1>
          </Item>
          <GroupItem caption="Mailing Address" captionRender={renderMailingAddressCaption}>
            <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
            <Item colSpan={1} dataField="address1">
              <Label text='Home Mailing Address' />
              <AddressAutoComplete
                autoCompleteTextBoxId={"autoCompleteTextBoxId"}
                updateAddressFields={updateAddressFields}
                value={member && member.address1}
                city={city}
                province={province}
                addressVerified={member && member.addressVerified}
                onEdit={() => {
                  addressEditedRef.current = true;
                  setIsDirty(true);
                }}
                onVerifiedChange={setAddressVerified}
                ref={addressAutoCompleteRef}
              />
            </Item>
            <EmptyItem visible={!isLarge} />
            <SimpleItem dataField='city' editorType='dxTextBox' colSpan={1} editorOptions={{
              readOnly: true,
              onValueChanged: (e) => {
                setCity(e.value);
              }
            }}>
              <Label text='City' />
            </SimpleItem>
            <SimpleItem
              dataField='provinceId'
              editorType='dxSelectBox'
              editorOptions={{...provinceEditorOptions, readOnly:true}}
            >
              <Label text='Province' />
            </SimpleItem>
            <SimpleItem dataField='postalCode' editorType='dxTextBox'
              editorOptions={{
                readOnly: true,
                onValueChanged: (e) => {
                }
              }}
            >
              <Label text='Postal Code' />
              <PatternRule
                pattern={appConst.postalCodePattern}
                message={appConst.postalCodeValidationMessage}
              />
            </SimpleItem>
          </GroupItem>
          <GroupItem cssClass="emails_phones">
            <ColCountByScreen xs={1} sm={1} md={2} lg={2} />
            <GroupItem key={`emails`} caption={'Email Addresses'}>
              {member && member.emailAddresses && member.emailAddresses.map((email, index) => (
                <GroupItem key={`emailGroup${index}`} cssClass="contact-card">
                  {/* Side by side, the type select is too narrow below ~1600px, so stack the rows. */}
                  <ColCountByScreen xs={1} sm={1} md={1} lg={isExtraLarge ? 2 : 1} />
                  <SimpleItem
                    key={`email${email.id}`}
                    dataField={`emailAddresses[${index}].address`}
                  >
                    <Label text={`Email ${index + 1}`} visible={false} />
                    <RequiredRule message="Email is required." />
                    <PatternRule
                      pattern={appConst.emailPattern}
                      message={appConst.emailValidationMessage}
                    />
                  </SimpleItem>
                 
                  <GroupItem>
                    <ColCountByScreen xs={6} sm={6} md={6} lg={6} />
                    <SimpleItem
                      key={`emailTypes${index}`}
                      colSpan={3}
                      editorType="dxSelectBox"
                      dataField={`emailAddresses[${index}].typeId`}
                      editorOptions={emailAddressTypeEditorOptions}
                    >
                      <Label text={`Email Type ${index + 1}`} visible={false} />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={2}
                      key={`preferredEmails${email.Id}`}
                      dataField={`emailAddresses[${index}].preferred`}
                      editorType="dxSwitch"
                      editorOptions={{
                        onValueChanged: (e) => {
                          if (typeof (e.event) !== 'undefined') {
                            setPreferredEmailOrPhone(index, 'emailAddresses');
                          }
                        }
                      }}
                    >
                      {/* On top: beside the switch, the label overflowed into the delete button's column. */}
                      <Label text="Preferred" location="top" />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={1}
                      name={`removeEmailButton${index}`}
                      itemType="button"
                      horizontalAlignment="right"
                      verticalAlignment="bottom"
                      buttonOptions={{
                        icon: 'trash',
                        hint: 'Remove email.',
                        onClick: () => {
                          if (email.preferred) {
                            showError("You can't delete a preferred email.")
                            return false;
                          }
                          let tmp = { ...member };
                          tmp.emailAddresses.splice(index, 1);
                          setMemberAndKeepScrollPosition(tmp);
                        }
                      }}>
                    </SimpleItem>
                  </GroupItem>
                </GroupItem>
              ))}
              <SimpleItem
                name="addEmailButton"
                itemType="button"
                horizontalAlignment="center"
                buttonOptions={{
                  icon: 'add',
                  text: 'Add Email',
                  onClick: () => {
                    let tmp = { ...member };
                    tmp.emailAddresses.push({ address: '', preferred: (member.emailAddresses && !member.emailAddresses.length) });
                    setMember(tmp);
                    requestAnimationFrame(() => {
                      let form = getFormInstance('memberForm');
                      let emailEditor = form.getEditor(`emailAddresses[${tmp.emailAddresses.length - 1}].address`);
                      if (typeof (emailEditor) !== 'undefined')
                        emailEditor.focus();
                    });
                  },
                }} />
            </GroupItem>
            <GroupItem key={`phones`} colCount={1} caption={'Phone Numbers'}>
              {member && member.phoneNumbers && member.phoneNumbers.map((phone, index) => (
                <GroupItem key={`phoneGroup${index}`} cssClass="contact-card">
                  <ColCountByScreen xs={1} sm={1} md={1} lg={isExtraLarge ? 2 : 1} />
                  <SimpleItem
                    key={`phone${phone.id}`}
                    dataField={`phoneNumbers[${index}].number`}
                    editorOptions={appConst.phoneEditorOptions}
                  >
                    <Label text={`Phone ${index + 1}`} visible={false} />
                    <RequiredRule message="Phone is required." />
                    <PatternRule
                      pattern={appConst.phone10DigitsPattern}
                      message={appConst.phoneEditorOptions.message}
                    />
                  </SimpleItem>
                  <GroupItem>
                    <ColCountByScreen xs={6} sm={6} md={6} lg={6} />
                  <SimpleItem
                    key={`phoneTypes${index}`}
                    colSpan={3}
                    editorType="dxSelectBox"
                    dataField={`phoneNumbers[${index}].typeId`}
                    editorOptions={phoneNumberTypeEditorOptions}
                  >
                    <Label text={`Phone Type ${index + 1}`} visible={false} />
                  </SimpleItem>
                  <SimpleItem
                      colSpan={2}
                      key={`preferredPhone${phone.Id}`}
                      dataField={`phoneNumbers[${index}].preferred`}
                      editorType="dxSwitch"
                      verticalAlignment="top"
                      editorOptions={{
                        onValueChanged: (e) => {
                          if (typeof (e.event) !== 'undefined') {
                            setPreferredEmailOrPhone(index, 'phoneNumbers');
                          }
                        }
                      }}
                    >
                      {/* On top: beside the switch, the label overflowed into the delete button's column. */}
                      <Label text="Preferred" location="top" />
                    </SimpleItem>
                    <SimpleItem
                      colSpan={1}
                      name={`removePhoneButton${index}`}
                      itemType="button"
                      horizontalAlignment="right"
                      verticalAlignment="bottom"
                      buttonOptions={{
                        icon: 'trash',
                        hint: 'Remove Phone.',
                        onClick: () => {
                          if (phone.preferred) {
                            showError("You can't delete a preferred phone.")
                            return false;
                          }
                          let tmp = { ...member };
                          tmp.phoneNumbers.splice(index, 1);
                          setMemberAndKeepScrollPosition(tmp);
                        }
                      }}>
                    </SimpleItem>
                  </GroupItem>
                </GroupItem>
              ))}
              <SimpleItem
                name="addPhoneButton"
                itemType="button"
                horizontalAlignment="center"
                buttonOptions={{
                  icon: 'add',
                  text: 'Add Phone',
                  onClick: () => {
                    let tmp = { ...member };
                    tmp.phoneNumbers.push({ address: '', preferred: (member.phoneNumbers && !member.phoneNumbers.length) });
                    setMember(tmp);
                    requestAnimationFrame(() => {
                      let form = getFormInstance('memberForm');
                      let phoneEditor = form.getEditor(`phoneNumbers[${tmp.phoneNumbers.length - 1}].number`);
                      if (typeof (phoneEditor) !== 'undefined')
                        phoneEditor.focus();
                    });
                  },
                }}
              />
            </GroupItem>
          </GroupItem>
        </GroupItem>
        <GroupItem cssClass="school_associations">
          <Item>
            <h1>Current Work Location(s)</h1>
          </Item>
          <Item render={() => {
            return (
              <CardContainer
                data={member && member.schools}
                updatePrinaryToFalse={updatePrimaryToFalse}
                updateSADivision={updateSADivision}
                updateSAStartDate={updateSAStartDate}
                updatePrinaryToTrue={updatePrinaryToTrue}
                addNewSA={addNewSA}
                userHasWorkLocation={member && member.schools.length > 0}
                showAddNewLocationButton={true}
              />
            )
          }}>
          </Item>
        </GroupItem>
        <GroupItem>
          <Item>
            <h1>Previous Work Location(s)</h1>
            <p className="section-note">If you have returned to work at one of these locations, please click “Add Another Work Location +” and indicate the date you returned to that location.</p>
          </Item>
          <Item render={() => { return (<CardContainer data={member && member.previousSchools} showAddNewLocationButton={false} />) }}>
          </Item>
        </GroupItem>
        {/* Always rendered (even with no designations) so the heading shows and the footer's
            Designations button has something to scroll to. */}
        <GroupItem colSpan={2} cssClass="designations">
          <Item>
            <h1>Designations</h1>
          </Item>
          <Item visible={!(member?.designations?.length > 0)}>
            <p className="empty-section-message">You have no designations.</p>
          </Item>
          {member && member.designations && member.designations.map((designation, index) => (
            <GroupItem key={`designation${index}`}>
              <ColCountByScreen xs={1} sm={1} md={5} lg={5} />
              <SimpleItem
                key={`designation.designation.description${index}`}
                dataField={`designations[${index}].designationId`}
                editorType="dxSelectBox"
                editorOptions={designationEditorOptions}
                valueExpr='value'
                displayExpr='text'
                selectByClick={true}
                selectionMode='single'
              >
                <Label text="Designation" />
                <RequiredRule message='Designation is required.' />
              </SimpleItem>
              <SimpleItem
                key={`designation.startDate${index}`}
                editorType="dxDateBox"
                dataField={`designations[${index}].startDate`}
                editorOptions={{ displayFormat: appConst.dateDisplayFormat }}>
                <RequiredRule message='Start date is required.' />
                <Label text="Start Date (YYYY-MM-DD)" />
              </SimpleItem>
              <SimpleItem
                key={`designation.endDate${index}`}
                editorType="dxDateBox"
                dataField={`designations[${index}].endDate`}
                editorOptions={{ displayFormat: appConst.dateDisplayFormat }} >
                <Label text="End Date (YYYY-MM-DD)" />
              </SimpleItem>
              <SimpleItem
                key={`designation.notes${index}`}
                editorType="dxTextBox"
                dataField={`designations[${index}].notes`}>
                <Label text="Notes" />
              </SimpleItem>
              <SimpleItem
                name={`removeDesignationButton${index}`}
                itemType="button"
                horizontalAlignment="left"
                verticalAlignment="bottom"
                buttonOptions={{
                  icon: 'trash',
                  hint: 'Remove Designation',
                  onClick: () => {
                    let tmp = { ...member };
                    tmp.designations.splice(index, 1);
                    setMemberAndKeepScrollPosition(tmp);
                  }
                }}>
              </SimpleItem>
            </GroupItem>
          ))}
        </GroupItem>
        <GroupItem colSpan={2}>
          <SimpleItem
            name="addDesignationButton"
            itemType="button"
            horizontalAlignment="center"
            buttonOptions={{
              text: 'New Designation',
              icon: 'add',
              onClick: () => {
                let tmp = { ...member };
                tmp.designations.push({});
                setMember(tmp);
                requestAnimationFrame(() => {
                  let form = getFormInstance('memberForm');
                  let emailEditor = form.getEditor(`designations[${tmp.designations.length - 1}].designationId`);
                  if (typeof (emailEditor) !== 'undefined')
                    emailEditor.focus();
                });
              },
            }}
          />
        </GroupItem>
      </Form>
  // Handlers inside are recreated each render but only close over these values.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [member, lookupData, isLarge, isExtraLarge, isSmall, isXSmall, city, province]);

  return (
    <div className="responsive-paddings" id="profile-member-wrapper">
      <AddressVerifiedContext.Provider value={addressVerified}>
        {memberForm}
      </AddressVerifiedContext.Provider>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={fetchingData || savingData}
        message={savingData ? "Saving Information" : "Loading..."}
        showIndicator={true}
        shading={true}
        showPane={true}
        closeOnOutsideClick={false}
      />
    </div>
  )
}